import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { apiService } from '../../api/apiService';
import { RootState } from '../store';
import { filterUniqueTokenUsers, store as storeGlobals } from './global';
import { ApiAuthResponse, ApiPasswordResetComplete, ApiUserProfile, ApiValidateAccountRequest2, ApiWebAuthRequest } from '@agunity/api-v4';
import { fetchProfile } from './fullProfile';
import { ApiAccountItemOut, ApiInternalAuthRequest } from '@agunity/api-v4/build/data-contracts';
import { GenericHttpResult } from '@agunity/api-v4/build/http-client';
import { ROOT_PATHS } from '../../Constants';
import { clearMemberCache } from './member';
import { clear } from './organizations';
import { clearCertificateCache } from './certificate';

export interface AuthState {
  error: any;
  securityToken: string | null;
  userToken: string | null;
  unlockMin: number | null;
  flags: string[];
}

const initialState: AuthState = {
  error: null,
  securityToken: null,
  userToken: null,
  unlockMin: null,
  flags: [],
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    login: (
      state: AuthState,
      action: PayloadAction<{ securityToken: string }>
    ) => {
      // token all done inside of api service
      state.securityToken = action.payload.securityToken;
    },
    selectUser: (
      state: AuthState,
      action: PayloadAction<{ userToken: string }>
    ) => {
      // userToken all done inside of api service
      state.userToken = action.payload.userToken;
    },
    logout: (state: AuthState) => {
      // we will leave user token set in case the user will come back
      state.securityToken = null;
    },
    failure: (state: AuthState, action: PayloadAction<any>) => {
      state.error = action.payload;
    },
    setUnlockMin: (state: AuthState, action: PayloadAction<any>) => {
      state.unlockMin = action.payload;
    },
    flags: (state: AuthState, action: PayloadAction<string[]>) => {
      state.flags = action.payload;
    }
  },
});

export const { login, logout, failure, selectUser, setUnlockMin, flags } = authSlice.actions;

export const selectIsAuthenticated = (state: RootState): boolean => !!state.auth.userToken;
export const selectUnlockMin = (state: RootState) => state.auth.unlockMin;
export const selectUserToken = (state: RootState) => state.auth.userToken;

export const asyncSelectUser =
  ({ userToken }: { userToken: string }) =>
    async (
      dispatch: Function,
      getState: () => RootState,
    ): Promise<void> => {
      apiService.userToken = userToken;
      dispatch(selectUser({ userToken }));

      // only init profile if we are authenticated
      if (getState().auth.securityToken && userToken) {
        // user change an => profile change so we need to reload
        dispatch(fetchProfile());
      }
    };

export const asyncLogin =
  (data: ApiWebAuthRequest) =>
    async (
      dispatch: Function,
    ): Promise<void> => {
      const apiAuthResponse: ApiAuthResponse = (await apiService.authenticateV20WebCreate(
        data
      ))?.data;

      if (apiAuthResponse.unlockMin) {
        dispatch(setUnlockMin(Math.abs(apiAuthResponse.unlockMin)));
        window.location.href = ROOT_PATHS.ACCOUNT_BLOCK;
      }
      if (!apiAuthResponse.securityToken) {
        dispatch(failure('invalid authentication'));
      }

      const allUsers = apiAuthResponse?.users ? apiAuthResponse.users : null;

      if (!allUsers || allUsers.length === 0) {
        dispatch(failure('user not found'));
      } else {
        dispatch(clearMemberCache());
        dispatch(clearCertificateCache());
        dispatch(clear());
        dispatch(storeGlobals({
          users: allUsers as ApiUserProfile[],
          identityId: apiAuthResponse.identityId as number,
        }));

        const filteredUsers = filterUniqueTokenUsers(allUsers);

        if (filteredUsers && filteredUsers.length === 1) {
          // is already filtered and will not be null
          const userToken = filteredUsers[0].token as string;
          apiService.userToken = userToken;
          await dispatch(asyncSelectUser({ userToken }));
        }

        dispatch(login({ securityToken: apiAuthResponse.securityToken as string }));
      }
    };

export const asyncLogout =
  () =>
    async (
      dispatch: Function,
    ): Promise<void> => {
      window.location.href = ROOT_PATHS.LOGIN;
      dispatch({ type: 'store/reset' });
      localStorage.clear();
      await apiService.logout();
      dispatch(logout());
      window.location.reload();
    };

export const authenticateAccount =
  (data: ApiValidateAccountRequest2) =>
    async (
    ) => {
      await apiService.authenticateV22ValidateCreate(
        data
      );
    };

export const resetPassword =
  (data: ApiPasswordResetComplete) =>
    async (
    ) => {
      await apiService.authenticateV20ResetPasswordCreate(
        data
      );
    };

export const getSSOAccountList =
  (headers: { Authorization: string }) =>
    async (): Promise<GenericHttpResult<ApiAccountItemOut[]>> => {
      return await apiService.authenticateV10AccountListList({ headers });
    };

export const AgInternalLogin =
  (data: ApiInternalAuthRequest, headers?: { Authorization: string }) =>
    async (): Promise<GenericHttpResult<ApiAuthResponse>> => {
      return await apiService.authenticateV10InternalLogonCreate(data, { headers });
    };
export default authSlice.reducer;